import React, {useContext, useState} from 'react';
import LocResourcesContext from "../../../common/src/contexts/LocResourcesContext";
import {SortingMode} from "../types";
import { sendEventToAdobe } from '../../../common/src/utils/adobeUtils';
import {Entry} from "../types"

type EntriesComponentProps = {
    entries: Entry[];
}

const EntriesComponent: React.FC<EntriesComponentProps> = ({entries}) => {
    const GLOBAL_PREFIX: string = "loy-bpo-overview";
    const years = [...new Set(entries.map(e => new Date(e.enteredDate).getUTCFullYear()))].sort().reverse();
    const { onFormatLocale, onFormatDate} = useContext(LocResourcesContext);
    const [ sortingMode, setSortingMode ] = useState<string>(SortingMode.Desc);
    const [year, setYear] = useState<number>(years[0])

    const renderEmptyEntriesTable = () => {
        return <div className="v3-flatbox--grey">
            <div className="ui-text--subheader">{onFormatLocale("mzp.points.empty")}</div>
        </div>
    }

    const toggleSortMode = () => {
        sortingMode === SortingMode.Desc
            ? setSortingMode(SortingMode.Asc)
            : setSortingMode(SortingMode.Desc)
    }

    const selectYearToFilter = (rawYear: string) => {
        setYear(parseInt(rawYear))
        sendEventToAdobe('clickTrack', '^tm:Loyalty^trk:Bonus Points^val:Account Page / Filter Year ' + rawYear);
    }

    const renderEntriesTable = () => {
        return <div data-grid-v3="grid grid-rows-2 gap-none" className={`${GLOBAL_PREFIX}_table`}>
            <div data-grid-v3="col-start-1 col-12 row-start-1 row-1">
                <div data-grid-v3="grid" className={`${GLOBAL_PREFIX}_table_header`}>
                    <div data-grid-v3="col-start-1 col-3 col-start-1@md col-2@md"
                         className={`${GLOBAL_PREFIX}_table_header_column ${GLOBAL_PREFIX}_table_header--clickable`}
                         onClick={() => { toggleSortMode(); sendEventToAdobe('clickTrack', '^tm:Loyalty^trk:Bonus Points^val:Account Page / Date Sorting ' + sortingMode); }}>
                        <span>{onFormatLocale("mzp.points.date")}</span>
                        <i className={`v5-icon--arrow-${(sortingMode === "desc") ? "down" : "up"} ${GLOBAL_PREFIX}_table_header_sort-icon`}> </i>
                    </div>
                    <div data-grid-v3="col-start-4 col-6 col-start-3@md col-8@md" className={`${GLOBAL_PREFIX}_table_header_column`}>
                        <span>{onFormatLocale("mzp.points.type")}</span>
                    </div>
                    <div data-grid-v3="col-start-10 col-3 col-start-11@md col-2@md" className={`${GLOBAL_PREFIX}_table_header_column`}>
                        <span>{onFormatLocale("mzp.points.points")}</span>
                    </div>
                </div>
            </div>
            <div data-grid-v3="col-start-1 col-12 row-start-2 row-1">
                {renderEntries()}
            </div>
        </div>
    }

    const renderEntries = () => {
        let entriesToRender = entries.slice(0);

        entriesToRender.sort((a, b) => {
            if (sortingMode === SortingMode.Asc) {
                return new Date(a.enteredDate).getTime() - new Date(b.enteredDate).getTime();
            } else {
                return new Date(b.enteredDate).getTime() - new Date(a.enteredDate).getTime();
            }
        });

        if(year){
            entriesToRender = entriesToRender.filter(e => new Date(e.enteredDate).getUTCFullYear() === year)
        }

        return entriesToRender.map(v => {
            const pending = (v.status === 'Inactive') ? <span> - {onFormatLocale("loy.points.description.P")}</span> : '';
            const description = (v.orderId) ?
                <span><a className={`ui-link ${GLOBAL_PREFIX}_table_body_column_link`}
                         href={`/account/orders/detail/${v.orderId}`}>{v.orderId}</a>:
                    {onFormatLocale(v.description) ? onFormatLocale(v.description) : v.description}{pending}</span> :
                <span>{onFormatLocale(v.description) ? onFormatLocale(v.description) : v.description}</span>;

            return <div data-grid-v3="grid" className={`${GLOBAL_PREFIX}_table_body ${v.type === 'Gift' ? "gift_entry" : ""}`}>
                <div data-grid-v3="col-start-1 col-3 col-start-1@md col-2@md" className={`${GLOBAL_PREFIX}_table_body_column`}>
                    {onFormatDate(new Date(v.enteredDate))}
                </div>
                <div data-grid-v3="col-start-4 col-6 col-start-3@md col-8@md" className={`${GLOBAL_PREFIX}_table_body_column`}>
                    {description}
                </div>
                <div data-grid-v3="col-start-10 col-3 col-start-11@md col-2@md"
                     className={
                         `${GLOBAL_PREFIX}_table_body_column 
                         ${GLOBAL_PREFIX}_table_body_column--${(v.amount < 0) ? "error" : "success"}
                         ${v.type === 'Gift' ? `${GLOBAL_PREFIX}_table_body_column--gift` : ""}
                         ${(v.status === 'Inactive') ? `${GLOBAL_PREFIX}_table_body_column--disabled` : ""}`
                     }>
                    {v.amount}
                </div>
            </div>
        });
    };

    const readerYearSelector = () => {
        let yearsOptions = years.map(y => <option value={y}>{y}</option>)
        return [
            <label data-grid-v3="col-start-8 col-3" data-utils-v3="padding-bottom--none padding-top--none"
                   className="hidden-xs v3-form__label v3-form__label--side">
                {onFormatLocale("mzp.filter.year")}:
            </label>
            ,
            <div data-grid-v3="col-start-11@md col-2@md col-start-8 col-5">
                <div className="v3-form">
                    <i className="v3-form__arrow">
                        <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg"><
                            path d="M12,14.9383422 L19.719699,7.21964518 C20.0126059,6.92676562 20.4874796,6.92678777 20.7803592,7.21969465 C21.0732387,7.51260153 21.0732166,7.98747526 20.7803097,8.28035482 L13.0607123,15.9989519 C12.4757342,16.5847646 11.5266219,16.5854407 10.9408019,16.0004618 L3.21969465,8.28035482 C2.92678777,7.98747527 2.92676562,7.51260153 3.21964518,7.21969465 C3.51252473,6.92678777 3.98739847,6.92676562 4.28030535,7.21964518 L12,14.9383422 Z" fill-rule="evenodd"/></svg>
                    </i>
                    <select id="year-select" className={`v3-form__select ${GLOBAL_PREFIX}--select`} name="year" value={year} onChange={(e) => selectYearToFilter(e.target.value)}>
                        {yearsOptions}
                    </select>
                </div>
            </div>
        ];
    }

    return (
        <div className={GLOBAL_PREFIX}>
            <div data-grid-v3="grid vertical-center gap-none" data-utils-v3="margin-bottom--sm@md margin-bottom--xs margin-top--sm@md margin-top--xs">
                <div data-grid-v3="col-start-1@md col-7@md col-start-1 col-7">
                    <h3 className="v3-h3" data-utils-v3="margin--none">{onFormatLocale("mzp.points.pointsoverview")}</h3>
                </div>

                {(years.length > 0) && readerYearSelector()}

            </div>

            {(entries.length === 0) && renderEmptyEntriesTable()}
            {(entries.length > 0) && renderEntriesTable()}
        </div>
    );
}

export default EntriesComponent;
